import React, { useState, useEffect, useCallback } from 'react';
import { Container, Stack, Table, Tag } from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import { useAuth } from '../../hooks/auth';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import api from '../../services/api';
import { toastSuccess, toastError } from '../../toasts';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import { CustomButton } from '../../components/CustomButton';
import { CustomCell } from '../../components/CustomCell';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import EditarCadastrar from './EditarCadastrar';
import { FaUsers } from 'react-icons/fa';

const { Column, Cell } = Table;

const MODULOS_LABEL = {
  pedidos: 'Pedidos',
  cardapio: 'Cardápio',
  configuracoes: 'Configurações',
  usuarios: 'Usuários',
  entregadores: 'Entregadores',
  cupons: 'Cupons',
  fidelidade: 'Fidelidade',
  promocoes: 'Promoções',
  banners: 'Banners',
  empresa: 'Empresa',
  relatorios: 'Relatórios',
  agendamento: 'Agendamento',
  chatbot: 'Chatbot',
};

const GruposAcesso = () => {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const atualizar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`grupo-acesso/listar/${id_empresa}`);
      setGrupos(data);
    } catch {
      toastError('Erro ao carregar grupos de acesso');
    } finally {
      setLoading(false);
    }
  }, [id_empresa]);

  useEffect(() => { atualizar(); }, [atualizar]);

  const handleEditar = (grupo) => {
    setSelected(grupo);
    setModalOpen(true);
  };

  const handleNovo = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleRemover = async (grupo) => {
    try {
      await api.delete(`grupo-acesso/remover/${grupo.Id}`);
      toastSuccess('Grupo removido com sucesso');
      atualizar();
    } catch (e) {
      toastError(e?.response?.data?.message || 'Erro ao remover grupo');
    }
  };

  const tableStyle = { background: theme.colors.backgroundSecondary, color: theme.colors.textSecondary };

  return (
    <Container
      style={{
        padding: 10,
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colors.background
      }}
    >
      <ContentHeader
        title="Grupos de Acesso de Usuários"
        subtitle="Gerencie os grupos de acesso de usuários da sua empresa"
        icon={FaUsers}
        thirdComponent={
          <>
            <DuvidasButton tela="grupo-acesso" />
            <CustomButton onClick={handleNovo}>
              <PlusIcon style={{ marginRight: 6 }} /> Novo Grupo
            </CustomButton>
          </>
        }
      />

      <Table
        height={window.innerHeight - 60 - 90}
        bordered
        loading={loading}
        data={grupos}
        autoHeight
        style={{ borderColor: theme.colors.backgroundSecondary, backgroundColor: theme.colors.backgroundSecondary }}
        locale={{ emptyMessage: 'Nenhum grupo cadastrado', loading: 'Carregando...' }}
      >
        <Column flexGrow={1} minWidth={160} style={tableStyle}>
          <CustomHeaderCell>Nome</CustomHeaderCell>
          <CustomCell dataKey="Nome" />
        </Column>

        <Column flexGrow={2} minWidth={260} style={tableStyle}>
          <CustomHeaderCell>Permissões</CustomHeaderCell>
          <Cell style={{ ...tableStyle, paddingTop: 8, paddingBottom: 8 }}>
            {(rowData) => {
              const perms = rowData.Permissoes || [];
              const visiveis = perms.slice(0, 4);
              const restantes = perms.length - visiveis.length;
              if (perms.length === 0) return <span style={{ color: theme.colors.textSecondary, fontSize: 12 }}>—</span>;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {visiveis.map((p) => (
                    <Tag key={p} size="sm" color="blue" style={{ fontSize: 11 }}>
                      {MODULOS_LABEL[p] || p}
                    </Tag>
                  ))}
                  {restantes > 0 && (
                    <Tag size="sm" style={{ fontSize: 11, backgroundColor: theme.colors.backgroundSecondary }}>
                      +{restantes}
                    </Tag>
                  )}
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column width={90} align="center" style={tableStyle}>
          <CustomHeaderCell>Ativo</CustomHeaderCell>
          <Cell style={tableStyle}>
            {(rowData) => (
              <div style={{
                width: 10, height: 10, borderRadius: '50%', margin: '0 auto',
                backgroundColor: rowData.Ativo ? '#4caf50' : '#f44336',
              }} />
            )}
          </Cell>
        </Column>

        <Column width={180} align="center" fixed="right" style={tableStyle}>
          <CustomHeaderCell>Opções</CustomHeaderCell>
          <Cell style={tableStyle}>
            {(rowData) => (
              <Stack spacing={8} justifyContent="center">
                <CustomButton size="xs" appearance="ghost" onClick={() => handleEditar(rowData)}>
                  Editar
                </CustomButton>
                <CustomButton size="xs" color="red" appearance="ghost" onClick={() => handleRemover(rowData)}>
                  Remover
                </CustomButton>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>

      {modalOpen && (
        <EditarCadastrar
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          grupo={selected}
          idEmpresa={id_empresa}
          onSaved={() => { setModalOpen(false); atualizar(); }}
        />
      )}
    </Container>
  );
};

export default GruposAcesso;
